import React from 'react';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import Table, { Column, TableActions } from 'components/Table';
import api, { ApiResponses } from 'lib/trpcClient';
import { RoleScope } from 'further-types/admin-user-role';
import ErrorBoundary from 'components/ErrorBoundary';
import PageFilters, { Filter } from 'components/PageFilters';
import SearchField from 'components/SearchField';
import {
  ADMIN_USER_ROLE_CREATE,
  createAdminUserRoleEditRoute,
} from 'adminConstants/routes';
import { Delete, Edit } from '@mui/icons-material';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useDisclosure, usePaginatedFilters } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';

const PAGE_ID = 'roles';
type Role = ApiResponses['adminUserRole']['list']['results'][number];

const AdminUserRolesContent: React.FC = () => {
  const { isSuperAdmin, firmId } = useGetRole();
  const deleteDisclosure = useDisclosure<{ roleId?: string }>();

  const { pagination, filters, handleChangeFilters } = usePaginatedFilters(
    PAGE_ID,
    {
      defaultFilters: {
        searchQuery: '',
        firmId,
      },
      defaultOrder: {
        orderBy: 'name' as const,
        order: 'asc' as const,
      },
      orderByOptions: ['name', 'createdAt', 'updatedAt'],
    },
  );

  const deleteRole = api.adminUserRole.deleteRole.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Role deleted successfully.',
      onSuccess: () => {
        deleteDisclosure.onClose();
      },
    }),
  );

  const [data, query] = api.adminUserRole.list.useSuspenseQuery({
    searchQuery: filters.searchQuery,
    ...pagination,
  });
  const roles = data.results;

  const columns: Array<Column<Role>> = [
    {
      label: 'Name',
      key: 'name',
    },
    {
      label: 'Realm',
      key: 'realm',
    },
    ...(isSuperAdmin
      ? [
          {
            label: 'Firm',
            key: 'firmName',
            render: (row: Role) =>
              row.scope === RoleScope.Firm && row.firmName
                ? row.firmName
                : 'System-wide',
          },
        ]
      : []),

    {
      label: 'Description',
      key: 'description',
    },
    {
      label: '',
      key: 'actions',
      render: (row) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              link: createAdminUserRoleEditRoute(row._id),
              icon: Edit,
              label: 'Edit',
              color: 'primary',
            },
            {
              icon: Delete,
              onClick: () => {
                deleteDisclosure.stageAction({ roleId: row._id });
              },
              label: 'Delete',
              color: 'error',
            },
          ]}
        />
      ),
      sort: false,
    },
  ];

  return (
    <>
      <PageFilters
        pageId={PAGE_ID}
        buttons={[
          {
            label: 'Add Role',
            link: ADMIN_USER_ROLE_CREATE,
          },
        ]}
      >
        <Filter>
          <SearchField
            autoSearchOnDebounce
            name="searchQuery"
            placeholder="Search by name"
            fullWidth
            value={filters.searchQuery}
            onSearch={(searchQuery) => {
              handleChangeFilters({ searchQuery });
            }}
            allowEmptyQuery
          />
        </Filter>
      </PageFilters>
      <Table
        columns={columns}
        loading={query.isFetching || pagination.isPaginationChanging}
        tablebody={roles}
      />
      <AlertDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={() => {
          deleteRole.mutate({
            roleId: deleteDisclosure.actionProps.roleId ?? '',
          });
        }}
        title="Delete Role"
        content="Are you sure you want to delete this role? Any users that currently have this role will lose it."
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Delete',
        }}
        confirmBtnProps={{
          disabled: deleteRole.isPending,
          color: 'secondary',
        }}
        cancelBtnProps={{
          disabled: deleteRole.isPending,
        }}
      />
    </>
  );
};

const AdminUserRolesPage: React.FC = () => {
  return (
    <PageContainer heading="Roles">
      <PageContent withSpacing>
        <ErrorBoundary>
          <AdminUserRolesContent />
        </ErrorBoundary>
      </PageContent>
    </PageContainer>
  );
};

export default AdminUserRolesPage;
